import { iris } from '@teachable/iris'
import { get } from 'lodash'
import { getItem } from 'src/util/cookies'
import { getPerformanceTimings } from 'student/secure/checkout/performance'
import {
  currentPaymentMethodSelector,
  orderAccountStatusSelector,
  unformattedPriceForOrderSelector,
} from './store/selectors'

class CheckoutTracker {
  storeAccessor = null

  deviceInfo = {}

  init = ({ userAgent, irisUrl, sessionId, schoolId, storeAccessor }) => {
    try {
      iris.init({
        source: 'fedora-client',
        url: irisUrl,
        noun: 'user',
        noun_id: '', // iris 1.6.X sets a noun_id using cross subdomain cookies
        selector: '',
      })
    } catch {
      console.error('iris not supported')
    }
    this.deviceInfo = { user_agent: userAgent, session_id: sessionId }
    this.storeAccessor = storeAccessor
    window.addEventListener('beforeunload', this.onPageUnload)
  }

  getOrderDetails = () => {
    if (!this.storeAccessor) return {}
    // @ts-ignore
    const store = this.storeAccessor()
    // This is a code smell because now the tracker contains knowledge on how to access store.
    // we can split this up when it becomes an issue

    return {
      order_token: get(store, 'order.token'),
      payment_method: currentPaymentMethodSelector(store),
      coupon_percent: null,
      course_id: get(store, 'order.course_id'),
      product_id: get(store, 'order.product_id'),
      user_status: orderAccountStatusSelector(store) || 'unknown',
      school_id: initHelper.getSchoolId() || get(store, 'order.school_id'),
      // TODO(nate): find out why the default is both 'unknown' here and 0 at other times
      product_price: unformattedPriceForOrderSelector(store) || 'unknown',
      saved_credit_card: !!get(
        store,
        'order.payment_methods.exisiting.length',
        0
      ),
    }
  }

  onPageUnload = () => {
    this.funnel.leavesCheckout()
  }

  vatId = {
    added: () => this.emit('vat_id_link_started'),
    entered: () => this.emit('vat_id_entered'),
    submitted: () => this.emit('vat_id_submitted'),
    successful: () => this.emit('vat_id_successful'),
    invalid: () => this.emit('vat_id_invalid'),
  }

  coupon = {
    added: () => this.emit('student_checkout_coupon_started'),
    entered: () => this.emit('student_checkout_coupon_entered'),
    submitted: () => this.emit('student_checkout_coupon_submitted'),
    successful: () => this.emit('student_checkout_coupon_successful'),
    invalid: () => this.emit('student_checkout_coupon_invalid'),
  }

  identity = {
    editEmail: () => this.emit('student_checkout_identify_email_entered'),
    editName: () => this.emit('student_checkout_identify_name_entered'),
    editPassword: () => this.emit('student_checkout_identify_password_entered'),
    pwdFieldVisible: () =>
      this.emit('student_checkout_identify_password_field_visible'),
    loginAttempt: () => this.emit('student_checkout_identify_login_attempted'),
    loginSuccessful: () =>
      this.emit('student_checkout_identify_login_successful'),
    consentToPromo: () =>
      this.emit('student_checkout_identify_promo_emails_consented'),
  }

  payment = {
    editCcNumber: this.emitOnce(
      'student_checkout_payment_info_cc_number_entered'
    ),
    editCcExp: this.emitOnce(
      'student_checkout_payment_info_cc_expiration_entered'
    ),
    editCcCvc: this.emitOnce('student_checkout_payment_info_cc_cvc_entered'),
    editCcCountry: this.emitOnce(
      'student_checkout_payment_info_cc_country_entered'
    ),
    editCcZip: this.emitOnce('student_checkout_payment_info_cc_zip_entered'),
    saveCard: () => this.emit('student_checkout_payment_info_cc_saved'),
    uncheckSaveCard: () =>
      this.emit('student_checkout_payment_info_cc_save_unchecked'),
    switchPayPal: () =>
      this.emit('student_checkout_payment_info_switch_to_paypal'),
  }

  funnel = {
    pageLoaded: this.emitOnce('student_checkout_funnel_checkout_loaded'),
    identityInfoComplete: this.emitOnce(
      'student_checkout_funnel_user_identified'
    ),
    paymentInfoComplete: this.emitOnce(
      'student_checkout_funnel_payment_info_completed'
    ),
    tosChecked: this.emitOnce('student_checkout_funnel_tos_checked'),
    purchaseRequestMade: () => {
      this.emit('student_checkout_funnel_purchase_requested')
      this.trackForDiscoverMarketingAds('clicked_purchase_button')
    },
    leavesCheckout: () => this.emit('student_checkout_funnel_left_checkout'),
    // authentication error from stripe
    // credit card error events are handled on the backend
    creditCardAuthenticationDeclined: (stripeError: {
      error: stripe.Error
    }) => {
      this.emit('student_checkout_funnel_credit_card_authentication_declined', {
        error_message: get(stripeError, 'error.message'),
        error_code: get(stripeError, 'error.code'),
      })
    },
    creditCardAuthenticationCompleted: () => {
      this.emit('student_checkout_funnel_credit_card_authentication_completed')
    },
    paymentMethodError: (
      stripeError: stripe.PaymentMethodResponse | { error: stripe.Error }
    ) => {
      this.emit('student_checkout_funnel_payment_method_failed', {
        error_message: get(stripeError, 'error.message'),
        error_code: get(stripeError, 'error.code'),
      })
    },
  }

  emit = (event, properties: { [key: string]: string } = {}) => {
    const orderDetails = this.getOrderDetails()
    const timing = getPerformanceTimings() || {}
    if (window.iris && typeof window.iris.emit === 'function') {
      window.iris.emit(event, {
        ...orderDetails,
        ...properties,
        ...this.deviceInfo,
        ...timing,
      })
    }
  }

  emitOnce(event) {
    let fired = false
    return () => {
      if (fired) return
      fired = true
      this.emit(event)
    }
  }

  trackForDiscoverMarketingAds = event => {
    // Facebook Discover Tracking
    // @ts-ignore
    if (typeof fbq === 'function') {
      // to avoid duplicated facebook calls https://developers.facebook.com/ads/blog/post/2017/11/28/event-tracking-with-multiple-pixels-tracksingle/
      // @ts-ignore
      fbq('trackSingle', '534878143288298', event)
    }

    // Google Discover Tracking
    // @ts-ignore
    if (typeof dataLayer === 'object') {
      // @ts-ignore
      dataLayer.push({ event })
    }
  }
}

export const checkoutTracker = new CheckoutTracker()

export const initHelper = {
  getIrisUrl: () => {
    const fedoraKeys = document.getElementById('fedora-keys')
    if (fedoraKeys) return fedoraKeys.getAttribute('data-iris-url') || ''
    return ''
  },
  getSchoolId: () => {
    const fedoraData = document.getElementById('fedora-data')
    if (fedoraData) {
      return String(fedoraData.getAttribute('data-school-id') || '')
    }
    return ''
  },
  getUserAgent: () => get(navigator, 'userAgent', ''),
  getSessionId: () => getItem('ahoy_visitor'),
}
